/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { alpha, darken, lighten, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { scrollBarColors } from '../styles';
import { component, getThemeTokens } from '../theme/tokens';

/** Class name that opts a surface (e.g. `Widget`) into the desktop glass styling. */
export const GLASS_SURFACE_CLASS = 'pa-glass-surface';

export const GLASS_RADIUS_PX = 22;

/** Tighter corner radius for the Home and project landing cards, matching their maps. */
export const LANDING_SURFACE_RADIUS_PX = 12;

/**
 * Gives every glass card inside a landing page the landing radius. The doubled class outranks
 * the desktop glass styling; pill-shaped bars keep their shape with `!important`.
 */
export const landingSurfaceSx = {
  [`& .${GLASS_SURFACE_CLASS}.${GLASS_SURFACE_CLASS}`]: {
    borderRadius: `${LANDING_SURFACE_RADIUS_PX}px`,
  },
} as const;

/** Hooks on `Widget`'s header parts, restyled only on the desktop glass surface. */
export const WIDGET_HEADER_CLASS = 'pa-widget-header';
export const WIDGET_TITLE_CLASS = 'pa-widget-title';
export const WIDGET_TITLE_ICON_CLASS = 'pa-widget-title-icon';

/** Where each aurora glow sits and how strong it is; the hues are `component.desktop.aurora`. */
const AURORA_GLOWS = [
  { shape: '900px 520px at 12% -4%', opacity: { dark: 0.42, light: 0.38 } },
  { shape: '760px 520px at 92% 8%', opacity: { dark: 0.3, light: 0.26 } },
  { shape: '900px 600px at 62% 104%', opacity: { dark: 0.22, light: 0.3 } },
  { shape: '640px 520px at 0% 78%', opacity: { dark: 0.16, light: 0.2 } },
] as const;

const auroraLayers = (mode: Theme['palette']['mode']) => {
  const { aurora, wallpaper } = component[mode].desktop;
  return [
    ...AURORA_GLOWS.map(
      ({ shape, opacity }, index) =>
        `radial-gradient(${shape}, ${alpha(aurora[index], opacity[mode])}, transparent 70%)`,
    ),
    `linear-gradient(180deg, ${lighten(wallpaper, 0.02)} 0%, ${wallpaper} 55%, ${darken(wallpaper, 0.04)} 100%)`,
  ];
};

/** Soft "aurora" desktop backdrop: coloured glows over a deep navy (dark) or pale blue-grey (light). */
export const getDesktopAurora = (mode: Theme['palette']['mode']) => auroraLayers(mode).join(', ');

/**
 * Desktop backdrop for Settings › Appearance › Desktop background: the full aurora, a subtle one
 * (a single glow) or one flat color.
 */
export const getDesktopBackground = (
  mode: Theme['palette']['mode'],
  variant: 'aurora' | 'subtle' | 'solid',
) => {
  const layers = auroraLayers(mode);
  const solid = component[mode].desktop.wallpaper;
  const base = layers[layers.length - 1];
  // One flat color, written as a gradient so it also works as a `background-image`.
  if (variant === 'solid') return `linear-gradient(${solid}, ${solid})`;
  if (variant === 'subtle') return [layers[0], base].join(', ');
  return getDesktopAurora(mode);
};

/** Frosted "liquid glass" surface for desktop widgets and bars. */
export const getGlassSurfaceSx = (theme: Theme): SystemStyleObject<Theme> => {
  const isDark = theme.palette.mode === 'dark';
  const palette = { ...getThemeTokens(theme), text: theme.palette.text };
  const { glass } = palette.component;
  const hairline = getGlassHairline(theme);
  return {
    borderRadius: `${GLASS_RADIUS_PX}px`,
    backgroundColor: 'transparent',
    backgroundImage: `linear-gradient(155deg, ${glass.top} 0%, ${glass.bottom} 100%)`,
    border: `1px solid ${glass.border}`,
    boxShadow: isDark
      ? `inset 0 1px 0 ${glass.highlight}, 0 30px 60px -28px ${palette.shadow.ambient}`
      : `inset 0 1px 0 ${glass.highlight}, 0 1px 2px ${palette.shadow.contact}, 0 28px 56px -28px ${palette.shadow.ambient}`,
    backdropFilter: 'blur(28px) saturate(170%)',
    WebkitBackdropFilter: 'blur(28px) saturate(170%)',
    // Roomier header with the icon in a soft tile, as in the v4 mockups.
    [`& .${WIDGET_HEADER_CLASS}`]: {
      height: 'auto',
      minHeight: 58,
      pt: '16px',
      pb: '12px',
      px: '18px',
    },
    [`& .${WIDGET_TITLE_CLASS}`]: { fontSize: 15, fontWeight: 600, gap: 1.25 },
    [`& .${WIDGET_TITLE_ICON_CLASS}`]: {
      mt: 0,
      width: 26,
      height: 26,
      borderRadius: '8px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: alpha(palette.text.primary, isDark ? 0.85 : 0.94),
      backgroundColor: palette.overlay.hover,
      '& svg': { fontSize: 16 },
    },
    // Record lists read like the BOD snapshot table: edge-to-edge rows split by hairlines.
    '& .MuiCardContent-root, & .MuiCardContent-root:last-child': { p: 0 },
    '& .MuiCardContent-root > .MuiDivider-root': { borderColor: hairline },
    '& .MuiList-root > .MuiListItem-root': { mb: 0 },
    '& .MuiList-root > .MuiListItem-root:not(:last-of-type)': {
      borderBottom: `1px solid ${hairline}`,
    },
    '& .MuiList-root .MuiListItemButton-root': { borderRadius: 0, px: 0 },
    '& .MuiTableCell-root': { borderColor: hairline },
  };
};

/** Hairline used between items that sit directly on the backdrop (KPI strip, scenario bar). */
export const getGlassHairline = (theme: Theme) => getThemeTokens(theme).border.hairline;

/** Small uppercase label above a value (KPI strip, scenario bar, stage bar). */
export const glassEyebrowSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
} as const;

/**
 * Dark pill that groups the top-right shell actions, each rendered as a small glass circle
 * (the v4 mockups' `.iconbtn.glass.soft`).
 */
export const getShellActionGroupSx = (theme: Theme): SystemStyleObject<Theme> => {
  const isDark = theme.palette.mode === 'dark';
  const palette = { ...getThemeTokens(theme), text: theme.palette.text };
  const { glass } = palette.component;
  const edge = isDark ? glass.border : palette.border.hairline;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    p: '3px',
    borderRadius: 999,
    backgroundColor: palette.background.well,
    border: `1px solid ${edge}`,
    boxShadow: `inset 0 1px ${isDark ? 6 : 2}px ${palette.shadow.contact}`,
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    '& .MuiBadge-root': { display: 'flex' },
    '& .MuiIconButton-root': {
      width: 30,
      height: 30,
      p: 0,
      borderRadius: '50%',
      color: alpha(palette.text.primary, isDark ? 0.75 : 0.84),
      backgroundImage: `linear-gradient(155deg, ${palette.overlay.hover}, transparent)`,
      border: `1px solid ${edge}`,
      boxShadow: `inset 0 1px 0 ${glass.highlight}`,
      transition: 'background-color 120ms ease, color 120ms ease',
      '&:hover': {
        color: palette.text.primary,
        backgroundColor: palette.overlay.pressed,
      },
      '& .MuiTouchRipple-root': { inset: 0, borderRadius: '50%' },
    },
    '& .MuiSvgIcon-root': { fontSize: 18 },
  };
};

/** Attribute `useScrollActivity` sets on a scroller while it is being scrolled. */
export const SCROLLING_ATTRIBUTE = 'data-scrolling';

const SCROLLBAR_THUMB_VAR = '--pa-autohide-scrollbar-thumb';

/**
 * Registers the thumb colour as a typed custom property. `scrollbar-color` itself does not
 * animate, but a registered `<color>` property does, which lets the thumb fade in and out.
 */
export const autoHideScrollbarGlobalStyles = {
  [`@property ${SCROLLBAR_THUMB_VAR}`]: {
    syntax: "'<color>'",
    inherits: 'false',
    initialValue: 'transparent',
  },
};

const SCROLLBAR_FADE_OUT = `${SCROLLBAR_THUMB_VAR} 700ms ease-out`;
const SCROLLBAR_FADE_IN = `${SCROLLBAR_THUMB_VAR} 150ms ease-in`;

/**
 * Thin scrollbar for the desktop widget page that fades in while scrolling and out once idle.
 * Only the page scroller hides: `scrollbar-color` inherits, so nested scrollers (widget lists,
 * tables) get the app's regular always-visible colours back. `transition` lists the page's
 * other transitions, which this has to extend rather than replace.
 */
export const getAutoHideScrollbarSx = (
  theme: Theme,
  transition: string,
): SystemStyleObject<Theme> => {
  const { mode } = theme.palette;
  const thumb = alpha(theme.palette.text.primary, 0.3);
  const scrolling = `&[${SCROLLING_ATTRIBUTE}="true"]`;
  return {
    [SCROLLBAR_THUMB_VAR]: 'transparent',
    transition: `${transition}, ${SCROLLBAR_FADE_OUT}`,
    scrollbarWidth: 'thin',
    scrollbarColor: `var(${SCROLLBAR_THUMB_VAR}) transparent`,
    [scrolling]: {
      [SCROLLBAR_THUMB_VAR]: thumb,
      transition: `${transition}, ${SCROLLBAR_FADE_IN}`,
    },
    // Safari still styles scrollbars only through the WebKit pseudo-elements.
    '&::-webkit-scrollbar': { width: 6, height: 6, backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 6,
      border: 0,
      backgroundColor: `var(${SCROLLBAR_THUMB_VAR})`,
    },
    '& *': { scrollbarColor: `${scrollBarColors[mode].thumb} transparent` },
  };
};
